import React, { useEffect, useState } from "react";
import "../../css/CRUDModal.css";
import { apiList } from "../../helpers/apiList";
import CustomAlert from "../../helpers/CustomAlert";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { formatDateToYYYYMMDD } from "../../helpers/DateHelpers";
import { useDispatch } from "react-redux";
import { setBalance } from "../store/balance/balanceSlice";

const TopUpSmsRcsModal = ({
  show,
  handleClose,
  callApi,
  editData,
  action,
  columnData,
}) => {
  const loggedInUserName = sessionStorage.getItem("username");

  const [formData, setFormData] = useState({
    currentSmsCount: "",
    currentRcsBalance: "",
    smsTopUp: "",
    rcsTopUp: "",
    closingSmsBalance: "",
    closingRcsBalance: "",
    adminRemark: "",
  });

  const [errors, setErrors] = useState({
    currentSmsCount: "",
    currentRcsBalance: "",
    smsTopUp: "",
    rcsTopUp: "",
    closingSmsBalance: "",
    closingRcsBalance: "",
    adminRemark: "",
  });

  const dispatch = useDispatch();

  useEffect(() => {
    // Calculate closing balances whenever smsTopUp or rcsTopUp changes
    setFormData((prev) => ({
      ...prev,
      closingSmsBalance: prev.smsTopUp
        ? parseInt(columnData?.smsBalance || 0) + parseInt(prev.smsTopUp || 0)
        : "",
      closingRcsBalance: prev.rcsTopUp
        ? parseInt(columnData?.rcsBalance || 0) + parseInt(prev.rcsTopUp || 0)
        : "",
    }));
  }, [formData.smsTopUp, formData.rcsTopUp, columnData]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;

    let updatedValue = value;

    setFormData((prev) => ({
      ...prev,
      [id]: updatedValue,
    }));
  };

  // Simple validation function
  const validate = () => {
    const newErrors = {};

    // Check if at least one of smsTopUp or rcsTopUp is filled
    if (!formData.smsTopUp && !formData.rcsTopUp) {
      newErrors.smsTopUp =
        "At least one of SMS Top Up or RCS Top Up is required";
      newErrors.rcsTopUp =
        "At least one of SMS Top Up or RCS Top Up is required";
    } else {
      // Validate SMS Top Up if provided
      if (formData.smsTopUp) {
        if (!/^\d+$/.test(formData.smsTopUp)) {
          newErrors.smsTopUp = "Only numbers are allowed for SMS Top Up";
        } else if (parseInt(formData.smsTopUp, 10) <= 0) {
          newErrors.smsTopUp = "Amount must be greater than zero";
        }
      }

      // Validate RCS Top Up if provided
      if (formData.rcsTopUp) {
        if (!/^\d+$/.test(formData.rcsTopUp)) {
          newErrors.rcsTopUp = "Only numbers are allowed for RCS Top Up";
        } else if (parseInt(formData.rcsTopUp, 10) <= 0) {
          newErrors.rcsTopUp = "Amount must be greater than zero";
        }
      }
    }

    if (!formData.adminRemark?.trim()) {
      newErrors.adminRemark = "Remarks are required";
    }

    return newErrors;
  };

  const handleBlur = (e) => {
    const { id, value } = e.target;
    const fieldErrors = {};
    const trimmedValue = value?.trim();

    // Clear previous errors for both fields
    fieldErrors.smsTopUp = "";
    fieldErrors.rcsTopUp = "";

    // Check if at least one field is filled
    if (!formData.smsTopUp && !formData.rcsTopUp && !trimmedValue) {
      fieldErrors.smsTopUp =
        "At least one of SMS Top Up or RCS Top Up is required";
      fieldErrors.rcsTopUp =
        "At least one of SMS Top Up or RCS Top Up is required";
    } else {
      // Validate the specific field being blurred
      switch (id) {
        case "smsTopUp":
          if (trimmedValue) {
            if (!/^\d+$/.test(trimmedValue)) {
              fieldErrors.smsTopUp = "Only numbers are allowed for SMS Top Up";
            } else if (parseInt(trimmedValue, 10) <= 0) {
              fieldErrors.smsTopUp = "Amount must be greater than zero";
            }
          }
          break;

        case "rcsTopUp":
          if (trimmedValue) {
            if (!/^\d+$/.test(trimmedValue)) {
              fieldErrors.rcsTopUp = "Only numbers are allowed for RCS Top Up";
            } else if (parseInt(trimmedValue, 10) <= 0) {
              fieldErrors.rcsTopUp = "Amount must be greater than zero";
            }
          }
          break;

        default:
          break;
      }
    }

    setErrors((prev) => ({
      ...prev,
      ...fieldErrors,
    }));
  };

  const handleBack = () => {
    handleClose();
    setErrors({});
  };

  const handleAddCourierModal = () => {
    const validationErrors = validate();
    setErrors(validationErrors);

    // If there are no validation errors, proceed to next step
    if (Object.keys(validationErrors).length === 0) {
      const payload = {
        username: loggedInUserName,
        customerCode: columnData?.customerCode || "",
        smsUnits: Number(formData.smsTopUp) || 0,
        rcsUnits: Number(formData.rcsTopUp) || 0,
        remarks: formData.adminRemark.trim(),
      };

      axiosPostApi(apiList.CREATE_FREE_TOPUP_REQUEST, payload)
        .then((response) => {
          if (response?.status === 200 && response?.data?.status === true) {
            CustomAlert({
              title: response?.data?.message,
              icon: "success",
              confirmButtonText: "OK",
              showCloseButton: true,
              allowOutsideClick: false,
            }).then((result) => {
              if (result.isConfirmed) {
                fetchDashboardData();
              }
            });
            callApi();
            handleClose();
            // handleClearData();
          } else if (response?.status === 200 && !response?.data?.status) {
            CustomAlert({
              title: "Failed to Request",
              text: response.data.message,
              icon: "error",
              confirmButtonText: "Retry",
            });
          }
        })
        .catch((error) => {
          console.error("Error processing Wallet request:", error);
          CustomAlert({
            title: "Error",
            text: "An error occurred while adding the Wallet request. Please try again later.",
            icon: "error",
            confirmButtonText: "Retry",
          });
        });
    }
  };

  const fetchDashboardData = () => {
    const endDate = formatDateToYYYYMMDD(new Date());
    const startDate1 = new Date(new Date().setDate(new Date().getDate() - 90));
    const finalStartDate = formatDateToYYYYMMDD(startDate1);

    const requestBody = {
      startDate: finalStartDate,
      endDate: endDate,
      username: loggedInUserName,
    };

    // setLoading(true);

    axiosPostApi(apiList.DASHBOARD_DATA, requestBody)
      .then((response) => {
        if (response?.status === 200 && response?.data?.respCode === "00") {
          dispatch(setBalance(response?.data?.data?.balanceDetails));
          callApi(1);
        } else {
          // Handle error case here if necessary
        }
      })
      .catch((error) => {
        console.error("Error fetching SMS overview data", error);
      });
  };

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1031,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                // width: "597px",
                width: "550px",
                borderRadius: "24px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                }}
              >
                <h5 className="modal-title main-title">TopUp SMS/RCS</h5>
              </div>
              <form>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "24px",
                  }}
                >
                  <div className="row mt-2">
                    <div>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="currentSmsCount">
                              Current SMS Balance (Qty)
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="currentSmsCount"
                              placeholder="0"
                              value={columnData?.smsBalance}
                              style={{
                                textAlign: "right",
                                cursor: "not-allowed",
                              }}
                              readOnly={true}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="currentRcsBalance">
                              Current RCS Balance (Qty)
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="currentRcsBalance"
                              placeholder="0.00"
                              //   value={Number(columnData?.rcsBalance).toFixed(2)}
                              value={columnData?.rcsBalance}
                              style={{
                                textAlign: "right",
                                cursor: "not-allowed",
                              }}
                              readOnly={true}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label htmlFor="smsTopUp">SMS Top Up</label>
                          <div className="form-group">
                            <input
                              type="text"
                              className="form-control placeholder:text-sm"
                              id="smsTopUp"
                              placeholder="Enter SMS units to top up"
                              value={formData.smsTopUp || ""}
                              style={{ textAlign: "right" }}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d{0,9}$/.test(value)) {
                                  handleInputChange(e);
                                }
                              }}
                              onBlur={handleBlur}
                              inputMode="numeric"
                            />
                            {errors.smsTopUp ? (
                              <span className="error">{errors.smsTopUp}</span>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="rcsTopUp">RCS Top Up</label>
                            <input
                              type="text"
                              className="form-control placeholder:text-sm"
                              id="rcsTopUp"
                              placeholder="Enter RCS units to top up"
                              value={formData.rcsTopUp}
                              style={{
                                textAlign: "right",
                              }}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d{0,9}$/.test(value)) {
                                  handleInputChange(e);
                                }
                              }}
                              onBlur={handleBlur}
                              inputMode="numeric"
                            />
                            {errors.rcsTopUp ? (
                              <span className="error">{errors.rcsTopUp}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="closingSmsBalance">
                              Closing SMS Balance (Qty)
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="closingSmsBalance"
                              placeholder="0.00"
                              value={formData?.closingSmsBalance || ""}
                              style={{
                                textAlign: "right",
                                cursor: "not-allowed",
                              }}
                              readOnly={true}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="closingRcsBalance">
                              Closing RCS Balance (Qty)
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="closingRcsBalance"
                              placeholder="0.00"
                              value={formData.closingRcsBalance}
                              style={{
                                textAlign: "right",
                                cursor: "not-allowed",
                              }}
                              readOnly={true}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-12">
                          <div className="form-group">
                            <label htmlFor="adminRemark">Remarks</label>
                            <textarea
                              className="form-control"
                              id="adminRemark"
                              placeholder="Enter remarks"
                              rows={3}
                              value={formData.adminRemark}
                              onChange={handleInputChange}
                              onBlur={(e) =>
                                setErrors((prev) => ({
                                  ...prev,
                                  adminRemark: e.target.value.trim()
                                    ? ""
                                    : "Remarks are required",
                                }))
                              }
                            />
                            {errors.adminRemark ? (
                              <span className="error">{errors.adminRemark}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <hr />
                    </div>
                  </div>
                </div>

                <div
                  className="scoped-modal mt-2"
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    padding: "12px",
                    paddingTop: "0px",
                    gap: "16px",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-back"
                    onClick={handleBack}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-submit"
                    onClick={handleAddCourierModal}
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopUpSmsRcsModal;
